import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuthState";
import { useToast } from "@/hooks/use-toast";
import { isUnauthorizedError } from "@/lib/authUtils";
import { apiRequest, queryClient, getQueryFn } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import Navbar from "@/components/navbar";
import Footer from "@/components/footer";
import { MobileTopBar } from "@/components/mobile/MobileTopBar";
import CountdownTimer from "@/components/countdown-timer";
import ReferFriend from "@/components/refer-friend";
import BetfredSignup from "@/components/betfred-signup";

import ShareAfterEntryPopup from "@/components/share-after-entry-popup";
import ReferralLinkPopup from "@/components/referral-link-popup";
import { ComponentLoading } from "@/components/ui/loading";
import { CompactPrizeBadges } from "@/components/challenges/CompactPrizeBadges";
// import ChatBubble from "@/components/ChatBubble";
import { Trophy, Users, Calendar, Gift, Play, Clock, Share2, UserPlus, Send, Loader2, FileText, AlertCircle, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";

// Helper function to transform competition data to PrizeDetails format
function transformCompetitionToPrizeDetails(competition: any) {
  const prizeDetails: any = {};

  // For competitions, we typically have only one winner, so we'll show the main prize
  // If there are multiple prizes, we'll show them in order
  if (competition?.prize1) {
    prizeDetails["1st"] = { item: competition.prize1, value: 0 };
  }
  if (competition?.prize2) {
    prizeDetails["2nd"] = { item: competition.prize2, value: 0 };
  }
  if (competition?.prize3) {
    prizeDetails["3rd"] = { item: competition.prize3, value: 0 };
  }

  // Fallback for old single prize field
  if (
    !competition?.prize1 &&
    !competition?.prize2 &&
    !competition?.prize3 &&
    competition?.prize
  ) {
    prizeDetails["1st"] = { item: competition.prize, value: 0 };
  }

  return prizeDetails;
}

export default function CompetitionDetail() {
  const params = useParams();
  const [, navigate] = useLocation();
  const { user, loading } = useAuth();
  const isAuthenticated = !!user;
  const { toast } = useToast();

  // All useState hooks must come before useEffect to maintain hook order
  const [answers, setAnswers] = useState<{ [key: number]: string }>({});
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [showReferralPopup, setShowReferralPopup] = useState(false);
  const [referralProcessed, setReferralProcessed] = useState(false);
  const [activeTab, setActiveTab] = useState("play");
  const [showInviteFriendsModal, setShowInviteFriendsModal] = useState(false);
  const [friendInvites, setFriendInvites] = useState([
    { id: 1, name: '', email: '', phone: '', sending: false },
  ]);
  const [couponCode, setCouponCode] = useState("");
  const [autoInvitePromptActive, setAutoInvitePromptActive] = useState(false);
  const invitePromptStorageKey = user?.id ? `invite_prompt_competition_${user.id}` : null;
  const [hasSeenInvitePrompt, setHasSeenInvitePrompt] = useState(true);

  const competitionId = parseInt((params as any).id as string);

  // Force mobile viewport to prevent 980px desktop fallback
  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !invitePromptStorageKey) {
      setHasSeenInvitePrompt(true);
      return;
    }
    try {
      const seen = window.localStorage.getItem(invitePromptStorageKey) === "true";
      setHasSeenInvitePrompt(seen);
    } catch {
      setHasSeenInvitePrompt(true);
    }
  }, [invitePromptStorageKey]);

  const {
    data: competition,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["/api/competitions", competitionId],
    retry: 3, // Retry failed requests
    retryDelay: 1000, // Wait 1 second between retries
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: true, // Always refetch on mount
  });

  const { data: userEntries } = useQuery({
    queryKey: ["/api/user/entries", competitionId],
    enabled: isAuthenticated,
    staleTime: 0, // Always fetch fresh data to show correct total entries
    refetchOnWindowFocus: true, // Refetch on focus to get latest entry counts
    refetchOnMount: true, // Always refetch on mount
  });

  // Query global Betfred verification status (if user has ever been verified)
  const { data: globalStatus } = useQuery<{ hasBeenVerified: boolean }>({
    queryKey: ["/api/betfred-global-status"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated,
    staleTime: 0, // Always fresh data
    refetchOnWindowFocus: true,
  });

  // Fetch user's existing answers with forced refresh
  const { data: existingAnswers } = useQuery({
    queryKey: ["/api/user/answers", competitionId],
    enabled: isAuthenticated,
    staleTime: 5000, // Consider data fresh for 5 seconds to prevent loops
    refetchOnWindowFocus: false, // Prevent excessive refetching
  });

  // Fetch user's referral code for sharing
  const { data: referralData } = useQuery({
    queryKey: ["/api/referral-code"],
    enabled: isAuthenticated,
  });

  const markInvitePromptSeen = useCallback(() => {
    if (typeof window === "undefined" || !invitePromptStorageKey) return;
    try {
      window.localStorage.setItem(invitePromptStorageKey, "true");
    } catch {
      // ignore
    }
    setHasSeenInvitePrompt(true);
  }, [invitePromptStorageKey]);

  const handleInviteModalClose = useCallback(() => {
    if (autoInvitePromptActive) {
      markInvitePromptSeen();
      setAutoInvitePromptActive(false);
    }
    setShowInviteFriendsModal(false);
    setFriendInvites([{ id: 1, name: '', email: '', phone: '', sending: false }]);
  }, [autoInvitePromptActive, markInvitePromptSeen]);

  // Calculate competition timing status (must be after competition data is fetched)
  // Use consistent timezone logic across the platform
  const now = new Date();
  const startDateOnly = (competition as any)
    ? new Date((competition as any).startDate).toISOString().split("T")[0]
    : null;
  const startTimeStr = (competition as any)?.startTime || "00:00";
  const startDateTime = startDateOnly
    ? new Date(`${startDateOnly}T${startTimeStr}:00+02:00`)
    : null;

  const endDateOnly = (competition as any)
    ? new Date((competition as any).endDate).toISOString().split("T")[0]
    : null;
  const endTimeStr = (competition as any)?.endTime || "23:59";
  const endDateTime = endDateOnly
    ? new Date(`${endDateOnly}T${endTimeStr}:59+02:00`)
    : null;

  const isExpired = endDateTime ? endDateTime < now : false;
  const isScheduled = startDateTime ? startDateTime > now : false;

  // Handle smooth scrolling to enter-competition section on hash navigation
  useEffect(() => {
    if (competition && window.location.hash === "#enter-competition") {
      // Wait for the component to fully render, then scroll
      setTimeout(() => {
        const element = document.getElementById("enter-competition");
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
        }
      }, 500); // Delay to ensure content is loaded
    }
  }, [competition]); // Re-run when competition data loads

  // Check if user has already entered competition
  const hasEnteredCompetition = useMemo(() => {
    // Handle different userEntries formats: object with totalEntries, or array
    if (!userEntries) return false;

    // If it's an object with totalEntries property
    if (typeof userEntries === 'object' && 'totalEntries' in userEntries) {
      return (userEntries as any).totalEntries > 0;
    }

    // If it's an array or object with entries array
    if (Array.isArray(userEntries)) {
      return userEntries.length > 0;
    }

    if (userEntries && typeof userEntries === 'object' && 'entries' in userEntries) {
      return Array.isArray((userEntries as any).entries) && (userEntries as any).entries.length > 0;
    }

    return false;
  }, [userEntries]);

  // Get total entry count for display - MUST BE DECLARED BEFORE ANY EARLY RETURNS
  const totalEntryCount = useMemo(() => {
    if (!userEntries) return 0;

    // If it's an object with totalEntries property (this is the correct format from server)
    if (typeof userEntries === 'object' && 'totalEntries' in userEntries) {
      return (userEntries as any).totalEntries;
    }

    // If it's an array (fallback)
    if (Array.isArray(userEntries)) {
      return userEntries.length;
    }

    // If it's an object with entries array (fallback)
    if (userEntries && typeof userEntries === 'object' && 'entries' in userEntries) {
      const entries = (userEntries as any).entries;
      return Array.isArray(entries) ? entries.length : 0;
    }

    return 0;
  }, [userEntries]);

  // Remove automatic showing of terms dialog - we'll show it when user tries to submit

  // Handle competition entry and referral code processing when page loads
  useEffect(() => {
    // Don't process anything for unauthenticated users - let them login first
    if (!isAuthenticated) {
      return;
    }

    // Return early if userEntries is still loading (undefined means query hasn't completed)
    if (userEntries === undefined) {
      return; // Wait for userEntries to load
    }

    // Only enter if user hasn't entered yet, competition exists, and not scheduled
    if (!referralProcessed && competition && !isScheduled && hasEnteredCompetition === false && (competition as any).isActive) {
      const urlParams = new URLSearchParams(window.location.search);
      const referralCode = urlParams.get("ref");

      // Only call the enter endpoint if competition has started
      apiRequest("POST", `/api/competitions/${competitionId}/enter`, {
        body: { referralCode: referralCode || undefined },
      })
        .then((data) => { // Changed response to data to access isFirstEntry
          setReferralProcessed(true);
          // Refresh data
          queryClient.invalidateQueries({
            queryKey: ["/api/user/entries", competitionId],
          });
          queryClient.invalidateQueries({ queryKey: ["/api/user/dashboard"] });
          queryClient.invalidateQueries({ queryKey: ["/api/competitions"] });

          // Use the isFirstEntry flag from the server response
          const isFirstEntry = data?.isFirstEntry === true;
          const isFirstEverEntry = data?.isFirstEverEntry === true;

          if (isFirstEverEntry) {
            // Very first competition entry ever - show referral popup
            setTimeout(() => {
              setShowReferralPopup(true);
            }, 1000);
          } else if (isFirstEntry) {
            // First entry to this specific competition - show share popup
            setTimeout(() => {
              setShowSharePopup(true);
            }, 1000);
          }

          // Show invite friends modal immediately after successful entry
          if (!hasSeenInvitePrompt) {
            setTimeout(() => {
              setAutoInvitePromptActive(true);
              setShowInviteFriendsModal(true);
            }, 1000); // Small delay to ensure UI is ready
          }
        })
        .catch((error) => {
          console.error("Error entering competition:", error);
          setReferralProcessed(true); // Still mark as processed to avoid loops
        });
    } else if (isScheduled || hasEnteredCompetition) {
      // Mark as processed if user has already entered or competition is scheduled
      setReferralProcessed(true);
    }
  }, [
    isAuthenticated,
    competitionId,
    referralProcessed,
    competition,
    isScheduled,
    hasEnteredCompetition,
    hasSeenInvitePrompt,
    userEntries // Added userEntries to dependencies
  ]);

  const submitAnswersMutation = useMutation({
    mutationFn: async (data: { answers: any[] }) => {
      return await apiRequest(
        "POST",
        `/api/competitions/${competitionId}/submit`,
        { body: data },
      );
    },
    onSuccess: (data: any) => {
      const entriesEarned = data?.entriesCreated || data?.answers?.length || 0;
      
      // Only show success toast if entries were actually earned
      if (entriesEarned > 0) {
        toast({
          title: "Success!",
          description: `You earned ${entriesEarned} new ${entriesEarned === 1 ? "entry" : "entries"}! Your answers have been submitted.`,
        });
      }

      // Simplified query invalidation - no need for both remove and invalidate
      queryClient.invalidateQueries({
        queryKey: ["/api/user/entries", competitionId],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/user/answers", competitionId],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/user/dashboard"] });

      // Use the isFirstEntry flag from the server response
      const isFirstEntry = data?.isFirstEntry === true;
      console.log("First entry check from server:", {
        isFirstEntry,
        response: data,
      });

      // Clear local answers state
      setAnswers({});

      // Only show share popup after answering questions - never invite friends modal here
      // The invite friends modal should have already been shown when they first joined
      console.log("Showing share popup after answering questions");
      setShowSharePopup(true);
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 500);
        return;
      }
      toast({
        title: "Error",
        description: error.message || "Failed to submit answers",
        variant: "destructive",
      });
    },
  });

  const redeemCouponMutation = useMutation({
    mutationFn: async (code: string) => {
      const response = await apiRequest(
        "POST",
        `/api/competitions/${competitionId}/coupons/redeem`,
        { body: { code } }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to redeem coupon");
      }
      return data;
    },
    onSuccess: (data: any) => {
      const entriesGranted = data?.entriesGranted || 0;
      toast({
        title: "Coupon applied",
        description:
          entriesGranted > 0
            ? `You received ${entriesGranted} bonus ${
                entriesGranted === 1 ? "entry" : "entries"
              } for this competition.`
            : "Coupon redeemed successfully.",
      });
      setCouponCode("");
      // Refresh entry counts on dashboard and competition detail
      queryClient.invalidateQueries({
        queryKey: ["/api/user/entries", competitionId],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/user/dashboard"] });
    },
    onError: (error: any) => {
      toast({
        title: "Coupon error",
        description: error?.message || "Failed to redeem coupon",
        variant: "destructive",
      });
    },
  });

  const handleAnswerChange = (questionId: number, answer: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const continueWithSubmission = async () => {
    const questions = (competition as any)?.questions || [];

    // Check if user has already answered all questions using existingAnswers
    const existingAnswersData = Array.isArray(existingAnswers)
      ? existingAnswers
      : (existingAnswers as any)?.answers ?? [];

    const answeredQuestionIds = new Set(
      existingAnswersData.map((answer: any) => answer.questionId)
    );
    const allQuestionIds = new Set(questions.map((q: any) => q.id));

    const allQuestionsAnsweredAlready = allQuestionIds.size > 0 &&
      allQuestionIds.size === answeredQuestionIds.size &&
      [...allQuestionIds].every(id => answeredQuestionIds.has(id));

    if (allQuestionsAnsweredAlready) {
      toast({
        title: "Already Submitted",
        description: "You have already answered all questions for this competition.",
        variant: "destructive",
      });
      return;
    }

    // Only submit answers for remaining questions
    const answersArray = (competition as any)?.questions
      ?.filter(
        (q: any) =>
          answers[q.id] && // Ensure there's an answer in the current state
          !answeredQuestionIds.has(q.id) // Ensure it's a new answer, not an already submitted one
      )
      .map((question: any) => ({
        questionId: question.id,
        answerText: answers[question.id],
      }));

    if (answersArray.length === 0) {
      toast({
        title: "No new answers to submit",
        description: "Please answer at least one new question before submitting",
        variant: "destructive",
      });
      return;
    }

    await submitAnswersMutation.mutateAsync({ answers: answersArray } as any);
  };

  const handleSubmit = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }

    const questions = (competition as any)?.questions || [];
    if (questions.some((q: any) => q.isRequired && !answers[q.id])) {
      toast({
        title: "Please answer all required questions",
        variant: "destructive",
      });
      return;
    }

    // If all conditions met, proceed with the submission logic
    continueWithSubmission();
  };

  // Handle sending friend invite to PrizeDrop
  const handleSendInvite = async (index: number) => {
    const friend = friendInvites[index];

    // Validate form fields
    if (!friend.name.trim()) {
      toast({
        title: "Error",
        description: "Please enter your friend's name",
        variant: "destructive",
      });
      return;
    }

    if (!friend.phone.trim()) {
      toast({
        title: "Error",
        description: "Please enter your friend's phone number",
        variant: "destructive",
      });
      return;
    }

    // Set sending state for this specific friend
    setFriendInvites(prev => prev.map((f, i) =>
      i === index ? { ...f, sending: true } : f
    ));

    try {
      const response = await apiRequest('POST', '/api/friends/invite-prizedrop', {
        body: {
          friendName: friend.name.trim(),
          friendEmail: friend.email.trim() || undefined,
          friendPhone: friend.phone.trim(),
          competitionId: competitionId // Send competition-specific referral link
        }
      });

      if (response.ok) {
        toast({
          title: "Invitation Sent!",
          description: `${friend.name} will receive an SMS invitation to join this competition`,
        });

        // Clear this friend's form fields
      setFriendInvites(prev => prev.map((f, i) =>
        i === index ? { ...f, name: '', email: '', phone: '', sending: false } : f
      ));

      if (autoInvitePromptActive) {
        handleInviteModalClose();
      }

      // Refresh friends list
      queryClient.invalidateQueries({ queryKey: ['/api/friends'] });
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.error || "Failed to send invitation",
          variant: "destructive",
        });
        setFriendInvites(prev => prev.map((f, i) =>
          i === index ? { ...f, sending: false } : f
        ));
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send invitation",
        variant: "destructive",
      });
      setFriendInvites(prev => prev.map((f, i) =>
        i === index ? { ...f, sending: false } : f
      ));
    }
  };

  // Update friend invite field
  const updateFriendInvite = (index: number, field: 'name' | 'email' | 'phone', value: string) => {
    setFriendInvites(prev => prev.map((f, i) =>
      i === index ? { ...f, [field]: value } : f
    ));
  };

  const hasSubmittedAnswers = (userEntries as any)?.entries?.some(
    (entry: any) => entry.entryType === "answer",
  ) || false;

  // Show loading state while data is being fetched
  if (isLoading || (!competition && !error)) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:max-w-[1280px]">
          <ComponentLoading text="Loading competition..." />
        </div>
        <Footer />
      </div>
    );
  }

  // Only show "not found" if we've finished loading and have no data
  if (!isLoading && !competition) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:max-w-[1280px]">
          <div className="text-center">
            <Trophy className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Competition Not Found
            </h1>
            <p className="text-gray-600 mb-6">
              The competition you're looking for doesn't exist.
            </p>
            <Button onClick={() => navigate("/competitions")}>
              View All Competitions
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Calculate completed questions for display
  const completedQuestions = (existingAnswers as any)?.length || 0;
  const remainingQuestions =
    (competition as any)?.questions?.filter(
      (q: any) =>
        !(existingAnswers as any)?.some((ans: any) => ans.questionId === q.id),
    ) || [];
  const totalReferralBonusEntries =
    (userEntries as any)?.entries?.filter((entry: any) => entry.entryType === "referral")
      .length || 0;
  const totalSocialBonusEntries =
    (userEntries as any)?.entries?.filter((entry: any) => entry.entryType === "share")
      .length || 0;
  const userAnswerIds =
    (existingAnswers as any)?.map((ans: any) => ans.questionId) || [];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <MobileTopBar />
      <Navbar />

      {/* Competition Header - Mobile Optimized */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-8 sm:py-12 lg:py-16">
        <div className="w-full mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block bg-white bg-opacity-20 rounded-full px-3 sm:px-4 py-1 sm:py-2 mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-medium uppercase tracking-wide">
                {(competition as any)?.type?.replace("_", " ")} Competition
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-3 sm:mb-4 leading-tight px-2">
              {(competition as any)?.title}
            </h1>
            {/* Action Buttons - Mobile Optimized Grid */}
            {isAuthenticated && (
              <div className="w-full max-w-4xl mx-auto px-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Invite Friends Button - PRIMARY CALL TO ACTION */}
                  <Button
                    size="lg"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-6 text-lg shadow-lg"
                    onClick={() => {
                      setAutoInvitePromptActive(false);
                      setShowInviteFriendsModal(true);
                    }}
                    data-testid="button-invite-friends-header"
                  >
                    <UserPlus className="h-5 w-5 mr-2" />
                    Invite your friends
                  </Button>

                  {/* Share Referral Link Button - SECONDARY ACTION */}
                  {referralData && (
                    <Button
                      variant="outline"
                      size="lg"
                      className="w-full border-2 border-white text-white bg-transparent hover:bg-white hover:text-blue-900 font-semibold py-6 text-lg"
                      onClick={() => {
                        // Scroll to refer-friend section
                        const referSection = document.getElementById("refer-friend-section");
                        if (referSection) {
                          referSection.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                            inline: "nearest",
                          });
                        }
                      }}
                      disabled={!(referralData as any)?.referralUrl}
                      data-testid="button-share-referral-link"
                    >
                      <Share2 className="h-5 w-5 mr-2" />
                      Share Referral Link to Socials
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Betfred Bonus Section - Top of page for unverified users */}
      {isAuthenticated &&
        (competition as any)?.betfredEnabled && !globalStatus?.hasBeenVerified && (
          <section className="py-8 bg-white border-b">
            <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 lg:max-w-[1280px]">
              <Button
                variant="default"
                size="lg"
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg font-semibold"
                onClick={() => {
                  setActiveTab("play");
                  // Scroll to the Betfred section after tab change
                  setTimeout(() => {
                    const betfredSection = document.querySelector(
                      '[data-testid="betfred-signup-section"]',
                    );
                    if (betfredSection) {
                      betfredSection.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    } else {
                      // Fallback: scroll to the tabs section
                      const tabsSection =
                        document.querySelector('[role="tablist"]');
                      if (tabsSection) {
                        tabsSection.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }
                    }
                  }, 150);
                }}
              >
                <Gift className="mr-2 h-5 w-5" />
                CLICK HERE TO GET 50 ENTRIES NOW
              </Button>
            </div>
          </section>
        )}

      {/* Main Content - Mobile Optimized */}
      <section className="py-6 sm:py-8 lg:py-12">
        <div className="max-w-7xl w-full mx-auto px-3 sm:px-4 md:px-6 lg:px-8 lg:max-w-[1280px]">
          <div className="w-full">
            {/* Questions Column */}
            <div>
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
                <Tabs
                  value={activeTab}
                  onValueChange={setActiveTab}
                  className="w-full"
                >
                  <TabsList className="grid w-full grid-cols-2 mb-4 sm:mb-6">
                    <TabsTrigger value="play" className="text-xs sm:text-sm">
                      Play
                    </TabsTrigger>
                    <TabsTrigger value="details" className="text-xs sm:text-sm flex items-center gap-1">
                      Details
                      {(competition as any)?.termsAndConditionsDialog && (
                        <AlertCircle className="h-3 w-3 text-amber-500" />
                      )}
                    </TabsTrigger>
                  </TabsList>

                  {/* Play Tab - Questions */}
                  <TabsContent value="play" className="space-y-4 sm:space-y-6">
                    <div className="bg-gray-50 rounded-lg sm:rounded-xl p-4 sm:p-6 relative">
                      {isAuthenticated && (
                        <>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-2 sm:space-y-0">
                            <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                              Answer Questions to Earn Entries
                            </h2>
                            <div className="bg-primary text-white rounded-full px-3 py-1 text-sm font-medium self-start">
                              {(competition as any)?.questions?.length || 0}{" "}
                              Questions
                            </div>
                          </div>
                          <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
                            Answer these questions to earn entries. Each question =
                            1 entry!
                          </p>
                        </>
                      )}

                      {isScheduled ? (
                        <div className="text-center py-8">
                          <Clock className="h-16 w-16 text-yellow-400 mx-auto mb-4" />
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            Competition Not Started
                          </h3>
                          <p className="text-gray-600 mb-6">
                            This competition hasn't started yet. Come back when
                            it begins!
                          </p>
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                            <div className="flex items-center justify-center gap-2 text-yellow-800">
                              <Calendar className="h-5 w-5" />
                              <span className="font-medium">
                                Starts:{" "}
                                {startDateTime?.toLocaleString("en-ZA", {
                                  timeZone: "Africa/Johannesburg",
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: false,
                                })}{" "}
                                (GMT+2)
                              </span>
                            </div>
                          </div>
                          <Button
                            onClick={() => navigate("/")}
                            variant="outline"
                          >
                            Back to Homepage
                          </Button>
                        </div>
                      ) : !isAuthenticated ? (
                        <div className="text-center py-8">
                          <Trophy className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            Registration Required
                          </h3>
                          <p className="text-gray-600 mb-6">
                            Please register or login to enter this competition
                          </p>
                          <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Button
                              className="bg-primary hover:bg-primary/90 text-white font-semibold shadow-lg"
                              onClick={() => {
                                // Get referral code from URL if present
                                const urlParams = new URLSearchParams(
                                  window.location.search,
                                );
                                const referralCode = urlParams.get("ref");

                                // Redirect to auth signup with both competition ID and referral code for COMPETITION-SPECIFIC entry
                                const authUrl = referralCode
                                  ? `/auth?tab=signup&redirect=/competition/${competitionId}&ref=${referralCode}&comp=${competitionId}`
                                  : `/auth?tab=signup&redirect=/competition/${competitionId}`;

                                navigate(authUrl);
                              }}
                            >
                              Register
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                // Get referral code from URL if present
                                const urlParams = new URLSearchParams(
                                  window.location.search,
                                );
                                const referralCode = urlParams.get("ref");

                                // Redirect to auth with both competition ID and referral code for COMPETITION-SPECIFIC entry
                                const authUrl = referralCode
                                  ? `/auth?redirect=/competition/${competitionId}&ref=${referralCode}&comp=${competitionId}`
                                  : `/auth?redirect=/competition/${competitionId}`;

                                navigate(authUrl);
                              }}
                            >
                              Login to Enter
                            </Button>
                          </div>
                        </div>
                      ) : isExpired ? (
                        <div className="text-center py-8">
                          <p className="text-gray-600 text-lg">
                            This competition has ended.
                          </p>
                        </div>
                      ) : isScheduled ? (
                        <div className="text-center py-8">
                          <Clock className="h-16 w-16 text-blue-500 mx-auto mb-4" />
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            Competition Starts Soon
                          </h3>
                          <p className="text-gray-600 mb-4">
                            This competition hasn't started yet. Come back when
                            it begins!
                          </p>
                          <div className="bg-blue-50 rounded-lg p-4">
                            <div className="text-sm text-blue-600 mb-2 font-medium">
                              Starts in:
                            </div>
                            <CountdownTimer
                              endDate={(competition as any)?.startDate}
                              endTime={(competition as any)?.startTime}
                              compact
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="space-y-2">
                            {(() => {
                              const totalQuestions =
                                (competition as any)?.questions?.length || 0;
                              const answeredCount =
                                (existingAnswers as any)?.length || 0;
                              const remainingCount =
                                totalQuestions - answeredCount;

                              return (
                                <div className="flex items-center gap-4 text-sm">
                                  <span className="text-green-600 font-medium">
                                    ✓ Completed: {answeredCount}
                                  </span>
                                  {remainingCount > 0 && (
                                    <span className="text-blue-600 font-medium">
                                      → Available: {remainingCount}
                                    </span>
                                  )}
                                  <span className="text-gray-500">
                                    Total: {totalQuestions}
                                  </span>
                                </div>
                              );
                            })()}
                          </div>

                          {/* Completed Questions (Greyed Out) */}
                          {completedQuestions > 0 && (
                            <div className="space-y-4">
                              <div className="flex items-center gap-2 mb-4">
                                <Trophy className="h-5 w-5 text-green-600" />
                                <h3 className="text-lg font-semibold text-green-800">
                                  Completed Questions ({completedQuestions})
                                </h3>
                              </div>
                              {(competition as any)?.questions
                                ?.filter((q: any) =>
                                  (existingAnswers as any)?.some(
                                    (ans: any) => ans.questionId === q.id,
                                  ),
                                )
                                .map((question: any, index: number) => {
                                  const answerText =
                                    (existingAnswers as any)?.find(
                                      (ans: any) =>
                                        ans.questionId === question.id,
                                    )?.answerText ||
                                    (existingAnswers as any)?.find(
                                      (ans: any) =>
                                        ans.questionId === question.id,
                                    )?.answer;
                                  return (
                                    <div
                                      key={question.id}
                                      className="p-4 bg-green-50 rounded-lg border border-green-200 opacity-75"
                                    >
                                      <div className="flex items-start gap-3">
                                        <div className="flex-shrink-0">
                                          <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                                            ✓
                                          </div>
                                        </div>
                                        <div className="flex-1">
                                          <Label className="text-sm font-medium text-green-800 mb-2 block">
                                            {index + 1}. {question.questionText}
                                          </Label>
                                          <div className="text-sm text-green-700 bg-green-100 p-2 rounded">
                                            <strong>Your answer:</strong>{" "}
                                            {answerText}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          )}



                      {/* Remaining Questions */}
                          {remainingQuestions.length > 0 && (
                            <div className="space-y-4">
                              {completedQuestions > 0 && (
                                <div className="flex items-center gap-2 mb-4">
                                  <Play className="h-5 w-5 text-blue-600" />
                                  <h3 className="text-lg font-semibold text-blue-800">
                                    Questions to Complete (
                                    {remainingQuestions.length})
                                  </h3>
                                  {remainingQuestions.length > 0 && (
                                    <Badge
                                      variant="outline"
                                      className="ml-2 bg-blue-50 text-blue-700 border-blue-200"
                                    >
                                      +{remainingQuestions.length} more entries
                                      available
                                    </Badge>
                                  )}
                                </div>
                              )}
                              {remainingQuestions.map(
                                (question: any, index: number) => (
                                  <div
                                    key={question.id}
                                    className="p-4 bg-blue-50 rounded-lg border border-blue-200"
                                  >
                                    <div className="flex items-start gap-3">
                                      <div className="flex-shrink-0">
                                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                                          {completedQuestions + index + 1}
                                        </div>
                                      </div>
                                      <div className="flex-1 space-y-3">
                                        <Label className="text-sm font-medium text-gray-700">
                                          {completedQuestions + index + 1}.{" "}
                                          {question.questionText}
                                        </Label>

                                        {question.questionType === "mcq" ? (
                                          <RadioGroup
                                            value={answers[question.id] || ""}
                                            onValueChange={(value) =>
                                              handleAnswerChange(
                                                question.id,
                                                value,
                                              )
                                            }
                                          >
                                            {question.options?.map(
                                              (
                                                option: string,
                                                optionIndex: number,
                                              ) => (
                                                <div
                                                  key={optionIndex}
                                                  className="flex items-center space-x-2"
                                                >
                                                  <RadioGroupItem
                                                    value={option}
                                                    id={`q${question.id}-${optionIndex}`}
                                                  />
                                                  <Label
                                                    htmlFor={`q${question.id}-${optionIndex}`}
                                                  >
                                                    {option}
                                                  </Label>
                                                </div>
                                              ),
                                            )}
                                          </RadioGroup>
                                        ) : (
                                          <Textarea
                                            value={answers[question.id] || ""}
                                            onChange={(e) =>
                                              handleAnswerChange(
                                                question.id,
                                                e.target.value,
                                              )
                                            }
                                            placeholder="Enter your answer..."
                                            rows={3}
                                          />
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                          )}

                          {/* Submit Button - Only show if there are remaining questions */}
                          {remainingQuestions.length > 0 && (
                            <Button
                              onClick={handleSubmit}
                              className="w-full bg-primary text-white hover:bg-primary/90 font-semibold"
                              size="lg"
                              disabled={submitAnswersMutation.isPending}
                            >
                              {submitAnswersMutation.isPending ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                  Submitting...
                                </>
                              ) : (
                                <>
                                  <Trophy className="mr-2 h-4 w-4" />
                                  Submit{" "}
                                  {remainingQuestions.length > 1
                                    ? "Answers"
                                    : "Answer"}{" "}
                                  ({remainingQuestions.length}{" "}
                                  {remainingQuestions.length > 1
                                    ? "entries"
                                    : "entry"}
                                  )
                                </>
                              )}
                            </Button>
                          )}

                          {/* Entry Summary */}
                          <Separator />
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium">
                                Total Entries:
                              </span>
                              <span className="text-lg font-bold text-primary">
                                {totalEntryCount}
                              </span>
                            </div>

                            <div className="text-sm text-gray-600">
                              <div>
                                • {completedQuestions} questions completed ={" "}
                                {completedQuestions} entries
                              </div>
                              {(() => {
                                const entryTypesCounts = (
                                  (userEntries as any)?.entries || []
                                ).reduce(
                                  (acc: Record<string, number>, entry: any) => {
                                    acc[entry.entryType] =
                                      (acc[entry.entryType] || 0) + 1;
                                    return acc;
                                  },
                                  {},
                                );

                                return (
                                  <>
                                    {entryTypesCounts.referral && (
                                      <div>
                                        • {entryTypesCounts.referral} referral
                                        bonus entries
                                      </div>
                                    )}
                                    {entryTypesCounts.betfred_signup && (
                                      <div>
                                        • {entryTypesCounts.betfred_signup}{" "}
                                        Betfred signup bonus entries
                                      </div>
                                    )}
                                    {entryTypesCounts.share && (
                                      <div>
                                        • {entryTypesCounts.share} social share
                                        bonus entries
                                      </div>
                                    )}
                                    {entryTypesCounts.coupon && (
                                      <div>
                                        • {entryTypesCounts.coupon} coupon bonus
                                        entries
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                              {remainingQuestions.length > 0 && (
                                <div>
                                  • {remainingQuestions.length} questions
                                  remaining = {remainingQuestions.length}{" "}
                                  potential entries
                                </div>
                              )}
                              <div>
                                {(competition as any)?.betfredEnabled && !globalStatus?.hasBeenVerified
                                  ? "• Verify Betfred account for 50 bonus entries"
                                  : globalStatus?.hasBeenVerified
                                    ? "• Betfred bonus: N/A (already verified)"
                                    : "• Share on social media for bonus entries"
                                }
                              </div>
                            </div>
                          </div>

                          {/* All questions completed message */}
                          {completedQuestions > 0 &&
                            remainingQuestions.length === 0 && (
                              <div className="text-center py-8">
                                <Trophy className="h-16 w-16 text-green-600 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-green-800 mb-2">
                                  All Questions Completed!
                                </h3>
                                <p className="text-green-700 mb-4">
                                  You've answered all {completedQuestions}{" "}
                                  questions in this competition.
                                </p>
                                <div className="bg-green-50 rounded-lg p-4">
                                  <div className="text-center">
                                    <div className="text-2xl font-bold text-primary mb-1">
                                      {totalEntryCount}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                      Total Entries
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                        </div>
                      )}


                      {/* Earn More Section - Only show for authenticated users */}
                      {isAuthenticated && (
                        <div className="mt-8 pt-8 border-t border-gray-200">
                          <h3 className="text-lg font-semibold text-gray-900 mb-6">Earn More Entries</h3>
                          <div className="space-y-6">
                            {(competition as any)?.betfredEnabled && !globalStatus?.hasBeenVerified && (
                              <div data-testid="betfred-signup-section">
                                <BetfredSignup
                                  competitionId={competitionId}
                                  title={(competition as any)?.title}
                                />
                              </div>
                            )}

                            {/* Refer a Friend Section */}
                            <div id="refer-friend-section">
                              <ReferFriend
                                competitionId={competitionId}
                                title={(competition as any)?.title || 'Competition'}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  {/* Details Tab - Terms & Conditions, Prize Details and Time Remaining */}
                  <TabsContent
                    value="details"
                    className="space-y-4 sm:space-y-6"
                  >
                    {/* Terms & Conditions Section - Higher Priority */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <FileText className="h-5 w-5" />
                          Terms & Conditions
                          {(competition as any)?.termsAndConditionsDialog && (
                            <div className="flex items-center gap-1 bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs">
                              <AlertCircle className="h-3 w-3" />
                              Read Me
                            </div>
                          )}
                          <a
                            href="/terms"
                            className="ml-auto inline-flex items-center gap-1 rounded-full border border-primary bg-primary/10 px-4 py-1 text-xs font-semibold text-primary transition hover:bg-primary/20"
                            target="_blank"
                            rel="noreferrer"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Read Full Terms
                          </a>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {(competition as any)?.termsAndConditionsDialog ? (
                          <div className="space-y-4">
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                              <div className="flex items-start gap-3">
                                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                <div>
                                  <h4 className="font-medium text-amber-800 mb-1">
                                    Special Terms Apply
                                  </h4>
                                  <p className="text-sm text-amber-700">
                                    This competition has specific terms and conditions that you must read and accept before participating.
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                              <div className="text-sm text-gray-700 space-y-3">
                                {(competition as any).termsAndConditionsDialog.split('\n').map((paragraph: string, index: number) => (
                                  paragraph.trim() && (
                                    <p key={index} className="leading-relaxed">
                                      {paragraph}
                                    </p>
                                  )
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <p className="text-gray-600">
                              By participating in this competition, you agree to our standard terms and conditions.
                            </p>
                            <div className="text-sm text-gray-500 space-y-2">
                              <p>• Participants must be 18 years or older</p>
                              <p>• One entry per person per competition unless otherwise stated</p>
                              <p>• Winners will be contacted within 7 days of competition closure</p>
                              <p>• Prizes are non-transferable and non-exchangeable</p>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <Gift className="h-5 w-5" />
                          Prize Details
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {/* Desktop: Image and prizes side by side, Mobile: Image top, prizes bottom */}
                        <div className="flex flex-col lg:flex-row lg:items-start lg:gap-6">
                          {/* Image Section */}
                          {(competition as any)?.bannerImageUrl && (
                            <div className="w-full lg:w-1/2 mb-4 lg:mb-0">
                              <img
                                src={(competition as any).bannerImageUrl}
                                alt={(competition as any).title}
                                className="w-full h-auto min-h-48 lg:min-h-56 object-contain rounded-lg"
                              />
                            </div>
                          )}

                          {/* Prize Section - Stacked format using separate fields */}
                          <div className="w-full lg:w-1/2">
                            <div className="space-y-3">
                              {(competition as any)?.prize1 && (
                                <div className="flex items-center gap-3">
                                  <Gift className="w-6 h-6 text-primary flex-shrink-0" />
                                  <span className="text-lg font-semibold text-primary">
                                    {(competition as any).prize1}
                                  </span>
                                </div>
                              )}
                              {(competition as any)?.prize2 && (
                                <div className="flex items-center gap-3">
                                  <Gift className="w-6 h-6 text-primary flex-shrink-0" />
                                  <span className="text-lg font-semibold text-primary">
                                    {(competition as any).prize2}
                                  </span>
                                </div>
                              )}
                              {(competition as any)?.prize3 && (
                                <div className="flex items-center gap-3">
                                  <Gift className="w-6 h-6 text-primary flex-shrink-0" />
                                  <span className="text-lg font-semibold text-primary">
                                    {(competition as any).prize3}
                                  </span>
                                </div>
                              )}
                              {/* Fallback for backward compatibility with old prize field */}
                              {!(competition as any)?.prize1 &&
                                !(competition as any)?.prize2 &&
                                !(competition as any)?.prize3 &&
                                (competition as any)?.prize && (
                                  <div className="flex items-center gap-3">
                                    <Gift className="w-6 h-6 text-primary flex-shrink-0" />
                                    <span className="text-lg font-semibold text-primary">
                                      {(competition as any).prize}
                                    </span>
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Desktop-only Coupon Code Section */}
                    {isAuthenticated && !isExpired && !isScheduled && (
                      <Card className="hidden lg:block">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-lg">
                            <Gift className="h-5 w-5" />
                            Have a coupon code?
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center gap-3">
                            <Input
                              placeholder="Enter coupon code"
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value)}
                              className="max-w-xs"
                              data-testid="competition-coupon-input"
                            />
                            <Button
                              onClick={() => {
                                const trimmed = couponCode.trim();
                                if (!trimmed || redeemCouponMutation.isPending) {
                                  return;
                                }
                                redeemCouponMutation.mutate(trimmed);
                              }}
                              disabled={
                                !couponCode.trim() ||
                                redeemCouponMutation.isPending
                              }
                              data-testid="competition-coupon-apply-button"
                            >
                              {redeemCouponMutation.isPending
                                ? "Applying..."
                                : "Apply Coupon"}
                            </Button>
                          </div>
                          <p className="mt-2 text-xs text-gray-500">
                            Coupon entries are added on top of your existing
                            entries for this competition.
                          </p>
                        </CardContent>
                      </Card>
                    )}

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">
                          {isExpired
                            ? "Competition Ended"
                            : isScheduled
                              ? "Starts In"
                              : "Time Remaining"}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {isExpired ? (
                          <div className="text-center py-8">
                            <p className="text-gray-600 text-lg">
                              This competition has ended.
                            </p>
                          </div>
                        ) : (
                          <CountdownTimer
                            endDate={(competition as any)?.endDate}
                            endTime={(competition as any)?.endTime}
                            startDate={(competition as any)?.startDate}
                            startTime={(competition as any)?.startTime}
                          />
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                </Tabs>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Referral Link Popup - Shows on first entry */}
      <ReferralLinkPopup
        isOpen={showReferralPopup}
        onClose={() => setShowReferralPopup(false)}
        competitionId={competitionId}
        competitionTitle={(competition as any)?.title || ""}
        referralUrl={
          (referralData as any)?.referralUrl
            ? `${(referralData as any).referralUrl}?comp=${competitionId}`
            : undefined
        }
      />

      {/* Share After Entry Popup - Shows on subsequent entries */}
      <ShareAfterEntryPopup
        isOpen={showSharePopup}
        onClose={() => setShowSharePopup(false)}
        competitionId={competitionId}
        competitionTitle={(competition as any)?.title || ""}
        referralUrl={
          (referralData as any)?.referralUrl
            ? `${(referralData as any).referralUrl}?comp=${competitionId}`
            : undefined
        }
      />

      {/* Invite Friend to PrizeDrop Modal */}
      <Dialog
        open={showInviteFriendsModal}
        onOpenChange={(open) => {
          if (open) {
            setShowInviteFriendsModal(true);
          } else {
            handleInviteModalClose();
          }
        }}
      >
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Invite a friend and both of you earn 35 bonus entries
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-4">
            {/* Header row */}
            <div className="grid grid-cols-12 gap-2 px-2 text-sm font-medium text-gray-700">
              <div className="col-span-3">Name</div>
              <div className="col-span-3">Email (Optional)</div>
              <div className="col-span-3">Phone Number</div>
              <div className="col-span-3"></div>
            </div>

            {/* Friend invite rows */}
            {friendInvites.map((friend, index) => (
              <div key={friend.id} className="grid grid-cols-12 gap-2 items-center">
                <input
                  type="text"
                  className="col-span-3 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="John Doe"
                  value={friend.name}
                  onChange={(e) => updateFriendInvite(index, 'name', e.target.value)}
                  data-testid={`input-friend-name-${index}`}
                />

                <input
                  type="email"
                  className="col-span-3 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="friend@example.com"
                  value={friend.email}
                  onChange={(e) => updateFriendInvite(index, 'email', e.target.value)}
                  data-testid={`input-friend-email-${index}`}
                />

                <input
                  type="tel"
                  className="col-span-3 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="27123456789"
                  value={friend.phone}
                  onChange={(e) => updateFriendInvite(index, 'phone', e.target.value)}
                  data-testid={`input-friend-phone-${index}`}
                />

                <Button
                  className="col-span-3"
                  onClick={() => handleSendInvite(index)}
                  disabled={friend.sending || !friend.name.trim() || !friend.phone.trim()}
                  data-testid={`button-send-invite-${index}`}
                >
                  {friend.sending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Invite
                    </>
                  )}
                </Button>
              </div>
            ))}

          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleInviteModalClose}
              data-testid="button-close-modal"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Chat Support Bubble */}
      {/* <ChatBubble /> */}
    </div>
  );
}
